import {
  ArchiveRowSchema,
  ArchivedCaseRowSchema,
  DeletedCaseRowSchema,
} from '@contracts/schemas/archive.schema';
import { pageOf } from '@contracts/schemas/common.schema';
import { CaseRecordSchema } from '@contracts/schemas/jobOrder.schema';
import type {
  ArchivedCaseFilter,
  ArchivedCasePage,
  ArchiveFilter,
  ArchivePage,
  ArchiveRepository,
  CaseRecord,
  DeletedCaseFilter,
  DeletedCasePage,
} from '@/application/ports/ArchiveRepository';
import { env } from '@/shared/config/env';
import { SaidError } from '@/shared/failure/failure';
import { request, send } from '../client';
import { getAccessToken } from '../tokenStore';

const ArchivePageSchema = pageOf(ArchiveRowSchema);
const DeletedCasePageSchema = pageOf(DeletedCaseRowSchema);
const ArchivedCasePageSchema = pageOf(ArchivedCaseRowSchema);

/**
 * The list and the export share this, so the file is the list on the screen.
 * `status` is Shipped or Received, the request of 5 October 2026 (third),
 * section 11; `all` is left off the query like every other open filter.
 */
function query(filter: ArchiveFilter): string {
  const params = new URLSearchParams();
  for (const key of [
    'q',
    'from',
    'to',
    'origin',
    'dentist',
    'product',
    'carrier',
    'status',
    'sort',
  ] as const) {
    const value = filter[key];
    if (value !== undefined && value !== '' && value !== 'all') params.set(key, value);
  }
  if (filter.page !== undefined) params.set('page', String(filter.page));
  if (filter.pageSize !== undefined) params.set('pageSize', String(filter.pageSize));
  const search = params.toString();
  return search === '' ? '' : `?${search}`;
}

export class HttpArchiveRepository implements ArchiveRepository {
  async list(filter: ArchiveFilter): Promise<ArchivePage> {
    return request(`/archive${query(filter)}`, { schema: ArchivePageSchema });
  }

  async record(jobOrderNo: string): Promise<CaseRecord> {
    const result = await request(`/archive/${jobOrderNo}`, { schema: CaseRecordSchema });
    return result as unknown as CaseRecord;
  }

  /**
   * CSV rather than JSON, so this one bypasses the schema parsing client. The
   * server decides which columns the caller may see: identity and value are
   * dropped from the header as well as the rows.
   */
  async exportCsv(filter: ArchiveFilter): Promise<Blob> {
    const token = getAccessToken();
    const response = await send(`${env.apiBaseUrl}/archive/export${query(filter)}`, {
      credentials: 'include',
      headers: token === null ? {} : { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new SaidError('The export could not be produced');
    return response.blob();
  }

  async deleted(filter: DeletedCaseFilter): Promise<DeletedCasePage> {
    const params = new URLSearchParams({ set: 'deleted' });
    for (const key of ['q', 'from', 'to', 'deletionStatus', 'origin'] as const) {
      const value = filter[key];
      if (value !== undefined && value !== '' && value !== 'all') params.set(key, value);
    }
    if (filter.page !== undefined) params.set('page', String(filter.page));
    if (filter.pageSize !== undefined) params.set('pageSize', String(filter.pageSize));

    return request(`/archive?${params.toString()}`, { schema: DeletedCasePageSchema });
  }

  /** The Archived tab. Section 4 of the request of 24 September 2026. */
  async archived(filter: ArchivedCaseFilter): Promise<ArchivedCasePage> {
    const params = new URLSearchParams({ set: 'archived' });
    for (const key of ['q', 'from', 'to', 'origin', 'sort'] as const) {
      const value = filter[key];
      if (value !== undefined && value !== '' && value !== 'all') params.set(key, value);
    }
    if (filter.page !== undefined) params.set('page', String(filter.page));
    if (filter.pageSize !== undefined) params.set('pageSize', String(filter.pageSize));

    return request(`/archive?${params.toString()}`, { schema: ArchivedCasePageSchema });
  }
}
